import React, { useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Form, Modal, Spinner, Table } from "react-bootstrap";
import API from "../services/api";
import "./VendorDispatches.css";

const reverseGeocode = async (lat, lng) => {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(
        lat
      )}&lon=${encodeURIComponent(lng)}`
    );
    if (!res.ok) return "";
    const data = await res.json();
    return data?.display_name || "";
  } catch {
    return "";
  }
};

const fmt = (v) =>
  v
    ? new Date(v).toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    : "—";

const tone = (status = "") => {
  const s = String(status).toLowerCase();
  if (s === "received" || s === "delivered") return "success";
  if (s === "returned") return "dark";
  if (s === "cancelled") return "danger";
  if (s === "delayed") return "warning";
  if (s === "dispatched" || s === "in-transit" || s === "out-for-delivery") return "info";
  return "secondary";
};

const canReceive = (status = "") => {
  const s = String(status).toLowerCase();
  return ["dispatched", "in-transit", "out-for-delivery", "delivered", "delayed"].includes(s);
};

const canReturn = (status = "") => {
  const s = String(status).toLowerCase();
  return ["dispatched", "in-transit", "out-for-delivery", "delivered", "delayed"].includes(s);
};

const quoteOf = (row) => {
  const q = row?.returnQuote || {};
  return {
    baseAmountRupees: Number(q.baseAmountRupees || 0),
    deductionPercent: Number(q.deductionPercent || 0),
    deductionAmountRupees: Number(q.deductionAmountRupees || 0),
    receivableAmountRupees: Number(q.receivableAmountRupees || 0),
  };
};

const requirementAmountOf = (row) =>
  Number(row?.requirementAmount ?? row?.returnQuote?.baseAmountRupees ?? 0);

export default function VendorDispatches() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [returnRow, setReturnRow] = useState(null);
  const [receiveForm, setReceiveForm] = useState({ currentLocation: "", note: "Material received by vendor" });
  const [returnForm, setReturnForm] = useState({
    reason: "",
    currentLocation: "",
    confirmReturn: false,
  });

  const load = async () => {
    try {
      const { data } = await API.get("/vendor/dispatches");
      setRows(Array.isArray(data) ? data : []);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to load dispatch details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openReceive = (row) => {
    setSelected(row);
    setReceiveForm({
      currentLocation: row?.dispatch?.currentLocation || "",
      note: "Material received by vendor",
    });
  };

  const closeReceive = () => {
    setSelected(null);
    setReceiveForm({ currentLocation: "", note: "Material received by vendor" });
  };

  const openReturn = (row) => {
    setReturnRow(row);
    setReturnForm({
      reason: "",
      currentLocation: row?.dispatch?.currentLocation || "",
      confirmReturn: false,
    });
  };

  const closeReturn = () => {
    setReturnRow(null);
    setReturnForm({
      reason: "",
      currentLocation: "",
      confirmReturn: false,
    });
  };

  const captureLocation = () => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported in this browser");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = Number(pos.coords.latitude || 0).toFixed(6);
        const lng = Number(pos.coords.longitude || 0).toFixed(6);
        const readable = await reverseGeocode(lat, lng);
        setReceiveForm((p) => ({ ...p, currentLocation: readable || "Location captured" }));
        setError("");
      },
      (err) => setError(err?.message || "Unable to capture current location"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const captureReturnLocation = () => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported in this browser");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = Number(pos.coords.latitude || 0).toFixed(6);
        const lng = Number(pos.coords.longitude || 0).toFixed(6);
        const readable = await reverseGeocode(lat, lng);
        setReturnForm((p) => ({ ...p, currentLocation: readable || "Location captured" }));
        setError("");
      },
      (err) => setError(err?.message || "Unable to capture current location"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const markReceived = async () => {
    if (!selected?._id) return;
    try {
      setSaving(true);
      await API.patch(`/vendor/dispatches/${selected._id}/receive`, {
        currentLocation: receiveForm.currentLocation,
        note: receiveForm.note,
      });
      closeReceive();
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to mark dispatch as received");
    } finally {
      setSaving(false);
    }
  };

  const markReturned = async () => {
    if (!returnRow?._id) return;
    if (!returnForm.confirmReturn) {
      setError("Please confirm deduction and return action");
      return;
    }
    try {
      setSaving(true);
      await API.patch(`/vendor/dispatches/${returnRow._id}/return`, {
        reason: returnForm.reason,
        currentLocation: returnForm.currentLocation,
        confirmReturn: true,
      });
      closeReturn();
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to return dispatch");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="vdp-page">
      <Card className="shadow-sm border-0 vdp-card">
        <Card.Body>
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <h5 className="mb-1">Dispatch Tracking</h5>
              <div className="text-muted small">View dispatch details and confirm received material</div>
            </div>
            <Button size="sm" variant="outline-primary" onClick={load}>
              Refresh
            </Button>
          </div>

          {error ? <Alert variant="danger" className="mt-3 mb-0">{error}</Alert> : null}

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : rows.length === 0 ? (
            <Alert className="mt-3 mb-0" variant="info">
              Dispatch details अभी available नहीं हैं.
            </Alert>
          ) : (
            <>
              <div className="table-responsive mt-3 d-none d-md-block">
                <Table hover bordered className="align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Requirement</th>
                      <th>Product</th>
                      <th>Amount</th>
                      <th>Tracking</th>
                      <th>Status</th>
                      <th>Location</th>
                      <th>Dispatch</th>
                      <th>ETA</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const d = r.dispatch || {};
                      return (
                        <tr key={r._id}>
                          <td>
                            <div className="fw-semibold">{`REQ-${String(r._id).slice(-6).toUpperCase()}`}</div>
                            <div className="small text-muted">{r.locationText || "—"}</div>
                          </td>
                          <td>{r.productType || "—"} ({r.quantity ?? "—"} {r.unit || ""})</td>
                          <td>{requirementAmountOf(r) > 0 ? `₹ ${requirementAmountOf(r).toLocaleString("en-IN")}` : "—"}</td>
                          <td>{d.trackingId || "—"}</td>
                          <td><Badge bg={tone(d.status)}>{d.status || "pending"}</Badge></td>
                          <td>{d.currentLocation || "—"}</td>
                          <td>{fmt(d.dispatchDate)}</td>
                          <td>{fmt(d.expectedDeliveryDate)}</td>
                          <td>
                            <div className="d-flex gap-2 flex-wrap">
                              <Button size="sm" variant="outline-primary" onClick={() => setDetailRow(r)}>
                                View Details
                              </Button>
                              {canReceive(d.status) ? (
                                <Button size="sm" onClick={() => openReceive(r)}>
                                  Mark Received
                                </Button>
                              ) : (
                                <span className="text-muted small">No action</span>
                              )}
                              {canReturn(d.status) ? (
                                <Button size="sm" variant="outline-danger" onClick={() => openReturn(r)}>
                                  Return
                                </Button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              <div className="vdp-mobile-list d-md-none mt-3">
                {rows.map((r) => {
                  const d = r.dispatch || {};
                  return (
                    <div key={r._id} className="vdp-item">
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        <div>
                          <div className="fw-semibold">{`REQ-${String(r._id).slice(-6).toUpperCase()}`}</div>
                          <div className="small text-muted">{r.productType || "—"}</div>
                        </div>
                        <Badge bg={tone(d.status)}>{d.status || "pending"}</Badge>
                      </div>
                      <div className="small mt-2"><strong>Tracking:</strong> {d.trackingId || "—"}</div>
                      <div className="small"><strong>Amount:</strong> {requirementAmountOf(r) > 0 ? `₹ ${requirementAmountOf(r).toLocaleString("en-IN")}` : "—"}</div>
                      <div className="small"><strong>Location:</strong> {d.currentLocation || "—"}</div>
                      <div className="small"><strong>Dispatch:</strong> {fmt(d.dispatchDate)}</div>
                      <div className="small"><strong>ETA:</strong> {fmt(d.expectedDeliveryDate)}</div>
                      <Button size="sm" variant="outline-primary" className="mt-2 me-2" onClick={() => setDetailRow(r)}>
                        View Details
                      </Button>
                      {canReceive(d.status) ? (
                        <Button size="sm" className="mt-2" onClick={() => openReceive(r)}>
                          Mark Received
                        </Button>
                      ) : null}
                      {canReturn(d.status) ? (
                        <Button size="sm" variant="outline-danger" className="mt-2 ms-2" onClick={() => openReturn(r)}>
                          Return
                        </Button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      <Modal show={!!detailRow} onHide={() => setDetailRow(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Dispatch Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailRow ? (
            <>
              <div className="vdp-detail-grid">
                <div>
                  <label>Requirement</label>
                  <div>{`REQ-${String(detailRow._id).slice(-6).toUpperCase()}`}</div>
                </div>
                <div>
                  <label>Product</label>
                  <div>{detailRow.productType || "—"} ({detailRow.quantity ?? "—"} {detailRow.unit || ""})</div>
                </div>
                <div>
                  <label>Status</label>
                  <div><Badge bg={tone(detailRow.dispatch?.status)}>{detailRow.dispatch?.status || "pending"}</Badge></div>
                </div>
                <div>
                  <label>Requirement Amount</label>
                  <div>
                    {requirementAmountOf(detailRow) > 0
                      ? `₹ ${requirementAmountOf(detailRow).toLocaleString("en-IN")}`
                      : "—"}
                  </div>
                </div>
                <div>
                  <label>Tracking ID</label>
                  <div>{detailRow.dispatch?.trackingId || "—"}</div>
                </div>
                <div>
                  <label>Transporter</label>
                  <div>{detailRow.dispatch?.transporterName || "—"}</div>
                </div>
                <div>
                  <label>Vehicle Number</label>
                  <div>{detailRow.dispatch?.vehicleNumber || "—"}</div>
                </div>
                <div>
                  <label>Driver Name</label>
                  <div>{detailRow.dispatch?.driverName || "—"}</div>
                </div>
                <div>
                  <label>Driver Mobile</label>
                  <div>{detailRow.dispatch?.driverMobile || "—"}</div>
                </div>
                <div>
                  <label>Dispatch Date</label>
                  <div>{fmt(detailRow.dispatch?.dispatchDate)}</div>
                </div>
                <div>
                  <label>Expected Delivery</label>
                  <div>{fmt(detailRow.dispatch?.expectedDeliveryDate)}</div>
                </div>
                <div>
                  <label>Current Location</label>
                  <div>{detailRow.dispatch?.currentLocation || "—"}</div>
                </div>
                <div>
                  <label>Assigned Employee</label>
                  <div>{detailRow.assignedEmployee?.fullName || "—"}</div>
                </div>
              </div>

              <div className="vdp-detail-block">
                <label>Dispatch Notes</label>
                <div>{detailRow.dispatch?.notes || "—"}</div>
              </div>
              <div className="vdp-detail-block">
                <label>Return Info</label>
                <div>
                  <div><strong>Base Amount:</strong> ₹{Number(detailRow.dispatch?.returnInfo?.baseAmountRupees || detailRow.returnQuote?.baseAmountRupees || 0).toLocaleString("en-IN")}</div>
                  <div><strong>Deduction %:</strong> {Number(detailRow.dispatch?.returnInfo?.deductionPercent || detailRow.returnQuote?.deductionPercent || 0).toLocaleString("en-IN")}%</div>
                  <div><strong>Amount Deducted:</strong> ₹{Number(detailRow.dispatch?.returnInfo?.amountDeducted || 0).toLocaleString("en-IN")}</div>
                  <div><strong>Receivable Amount:</strong> ₹{Number(detailRow.dispatch?.returnInfo?.receivableAmountRupees || detailRow.returnQuote?.receivableAmountRupees || 0).toLocaleString("en-IN")}</div>
                  <div><strong>Reason:</strong> {detailRow.dispatch?.returnInfo?.reason || "—"}</div>
                  <div><strong>Confirmed At:</strong> {fmt(detailRow.dispatch?.returnInfo?.confirmedAt)}</div>
                </div>
              </div>
              <div className="vdp-detail-block">
                <label>Requirement Notes</label>
                <div>{detailRow.notes || "—"}</div>
              </div>

              <div className="vdp-detail-block">
                <label>Tracking Timeline</label>
                {Array.isArray(detailRow.dispatch?.events) && detailRow.dispatch.events.length ? (
                  <div className="vdp-timeline">
                    {detailRow.dispatch.events
                      .slice()
                      .sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0))
                      .map((ev, idx) => (
                        <div key={`${ev.at || idx}-${idx}`} className="vdp-timeline-item">
                          <div className="d-flex justify-content-between gap-2">
                            <strong>{ev.status || "update"}</strong>
                            <span className="text-muted small">{fmt(ev.at)}</span>
                          </div>
                          {ev.location ? <div className="small"><strong>Location:</strong> {ev.location}</div> : null}
                          {ev.note ? <div className="small"><strong>Note:</strong> {ev.note}</div> : null}
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="text-muted small">No timeline updates yet.</div>
                )}
              </div>
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDetailRow(null)}>
            Close
          </Button>
          {detailRow && canReceive(detailRow.dispatch?.status) ? (
            <div className="d-flex gap-2">
              <Button
                onClick={() => {
                  setDetailRow(null);
                  openReceive(detailRow);
                }}
              >
                Mark Received
              </Button>
              {canReturn(detailRow.dispatch?.status) ? (
                <Button
                  variant="outline-danger"
                  onClick={() => {
                    setDetailRow(null);
                    openReturn(detailRow);
                  }}
                >
                  Return
                </Button>
              ) : null}
            </div>
          ) : null}
        </Modal.Footer>
      </Modal>

      <Modal show={!!returnRow} onHide={closeReturn} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Return With Deduction</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div className="vdp-return-quote mb-3">
              <div><strong>Base Amount:</strong> ₹{quoteOf(returnRow).baseAmountRupees.toLocaleString("en-IN")}</div>
              <div><strong>Deduction:</strong> {quoteOf(returnRow).deductionPercent.toLocaleString("en-IN")}%</div>
              <div><strong>Deducted Amount:</strong> ₹{quoteOf(returnRow).deductionAmountRupees.toLocaleString("en-IN")}</div>
              <div><strong>Final Receivable:</strong> ₹{quoteOf(returnRow).receivableAmountRupees.toLocaleString("en-IN")}</div>
            </div>
            <Form.Group className="mb-3">
              <Form.Label className="d-flex align-items-center justify-content-between">
                Return Location
                <button type="button" className="vdp-link-btn" onClick={captureReturnLocation}>
                  Use Current
                </button>
              </Form.Label>
              <Form.Control
                value={returnForm.currentLocation}
                onChange={(e) => setReturnForm((p) => ({ ...p, currentLocation: e.target.value }))}
                placeholder="Current location"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Reason</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={returnForm.reason}
                onChange={(e) => setReturnForm((p) => ({ ...p, reason: e.target.value }))}
                placeholder="Reason for return (optional)"
              />
            </Form.Group>
            <Form.Check
              type="checkbox"
              checked={returnForm.confirmReturn}
              onChange={(e) => setReturnForm((p) => ({ ...p, confirmReturn: e.target.checked }))}
              label={`Confirm: ${quoteOf(returnRow).deductionPercent.toLocaleString("en-IN")}% deduction (₹${quoteOf(returnRow).deductionAmountRupees.toLocaleString("en-IN")}) and final receivable ₹${quoteOf(returnRow).receivableAmountRupees.toLocaleString("en-IN")} for return.`}
            />
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeReturn}>
            Cancel
          </Button>
          <Button variant="danger" onClick={markReturned} disabled={saving}>
            {saving ? "Processing..." : "Confirm Return"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!selected} onHide={closeReceive} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Received</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="d-flex align-items-center justify-content-between">
                Current Location
                <button type="button" className="vdp-link-btn" onClick={captureLocation}>
                  Use Current
                </button>
              </Form.Label>
              <Form.Control
                value={receiveForm.currentLocation}
                onChange={(e) => setReceiveForm((p) => ({ ...p, currentLocation: e.target.value }))}
                placeholder="Current location"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Note</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={receiveForm.note}
                onChange={(e) => setReceiveForm((p) => ({ ...p, note: e.target.value }))}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeReceive}>
            Cancel
          </Button>
          <Button onClick={markReceived} disabled={saving}>
            {saving ? "Saving..." : "Confirm Received"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
